// Administración de dispositivos de cartelería para TV (POS → Configuración → TV / Cartelería).
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarClock, Copy, ExternalLink, Loader2, MonitorPlay, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { apiClient, getErrorMessage } from '@/lib/api/client';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Campo, Modal, Switch, fieldCls } from './shared';
import { LOCALE } from '@/lib/utils/regional';

const rpc = async <T,>(fn: string, body: Record<string, unknown> = {}): Promise<T> =>
  (await apiClient.post<T>(`/rpc/${fn}`, body)).data;

interface Dispositivo { id: string; nombre: string; token: string; caja_id: string | null; caja: string | null; plantilla_id: string | null; plantilla: string | null; es_activo: boolean; last_seen: string | null }
interface Caja { id: string; nombre: string }
interface Plantilla { id: string; nombre: string }

const tvUrl = (token: string) => `${window.location.origin}/tv/${token}`;
const fmt = (s: string | null) => (s ? new Date(s).toLocaleString(LOCALE, { dateStyle: 'short', timeStyle: 'short' }) : 'nunca');
const vacio = { id: '', nombre: '', caja_id: '', plantilla_id: '', es_activo: true };

export function TvDispositivos() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const { data: rows = [], isFetching, error } = useQuery<Dispositivo[]>({ queryKey: ['tvDisp'], queryFn: () => rpc<Dispositivo[]>('tv_dispositivos') });
  const { data: cajas = [] } = useQuery<Caja[]>({
    queryKey: ['posCajas'],
    queryFn: async () => (await apiClient.get<Caja[]>('/rest/pos.cajas?select=id,nombre&deleted_at=is.null&order=nombre')).data,
  });
  const { data: plantillas = [] } = useQuery<Plantilla[]>({ queryKey: ['tvPlantillas'], queryFn: () => rpc<Plantilla[]>('tv_plantillas') });
  const [form, setForm] = useState<typeof vacio | null>(null);
  const [progDisp, setProgDisp] = useState<Dispositivo | null>(null);

  const guardar = useMutation({
    mutationFn: (f: typeof vacio) => rpc('tv_dispositivo_guardar', { _data: { id: f.id || undefined, nombre: f.nombre, caja_id: f.caja_id || null, plantilla_id: f.plantilla_id || null, es_activo: f.es_activo } }),
    onSuccess: () => { toast.success('Dispositivo guardado'); setForm(null); void qc.invalidateQueries({ queryKey: ['tvDisp'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const eliminar = useMutation({
    mutationFn: (id: string) => rpc('tv_dispositivo_eliminar', { _id: id }),
    onSuccess: () => { toast.success('Dispositivo eliminado'); void qc.invalidateQueries({ queryKey: ['tvDisp'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const copiar = (token: string) => { void navigator.clipboard?.writeText(tvUrl(token)); toast.success('URL copiada'); };

  if (error) return <div className="p-6 text-sm text-urgente">{getErrorMessage(error)}</div>;

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold flex items-center gap-2"><MonitorPlay className="w-5 h-5 text-primary" /> TV / Cartelería</h1>
          <p className="text-sm text-muted-foreground">Dispositivos de pantalla por caja. Abre la URL en la TV (modo pantalla completa).</p>
        </div>
        <Button onClick={() => setForm({ ...vacio })}><Plus className="w-4 h-4 mr-1" /> Nuevo dispositivo</Button>
      </div>

      <Card>
        <CardContent className="overflow-x-auto pt-4">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">Nombre</th><th className="py-2 pr-3">Caja</th><th className="py-2 pr-3">Plantilla</th><th className="py-2 pr-3">URL para la TV</th>
              <th className="py-2 pr-3">Activo</th><th className="py-2 pr-3">Última señal</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {rows.length === 0 && !isFetching && <tr><td colSpan={7} className="py-8 text-center text-muted-foreground">Sin dispositivos</td></tr>}
              {rows.map((d) => (
                <tr key={d.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3 font-medium">{d.nombre}</td>
                  <td className="py-2 pr-3">{d.caja || '—'}</td>
                  <td className="py-2 pr-3">{d.plantilla || '—'}</td>
                  <td className="py-2 pr-3">
                    <div className="flex items-center gap-1">
                      <code className="truncate max-w-[18rem] text-xs text-muted-foreground">{tvUrl(d.token)}</code>
                      <button className="text-muted-foreground hover:text-primary" title="Copiar" aria-label="Copiar" onClick={() => copiar(d.token)}><Copy className="w-4 h-4" /></button>
                      <a className="text-muted-foreground hover:text-primary" title="Abrir" href={tvUrl(d.token)} target="_blank" rel="noopener noreferrer"><ExternalLink className="w-4 h-4" /></a>
                    </div>
                  </td>
                  <td className="py-2 pr-3">{d.es_activo ? 'Sí' : 'No'}</td>
                  <td className="py-2 pr-3 text-muted-foreground">{fmt(d.last_seen)}</td>
                  <td className="py-2 text-right whitespace-nowrap">
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Programación por horario" aria-label="Programación"
                      onClick={() => setProgDisp(d)}><CalendarClock className="w-4 h-4" /></button>
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted ml-1" title="Editar" aria-label="Editar"
                      onClick={() => setForm({ id: d.id, nombre: d.nombre, caja_id: d.caja_id ?? '', plantilla_id: d.plantilla_id ?? '', es_activo: d.es_activo })}><Pencil className="w-4 h-4" /></button>
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 ml-1" title="Eliminar" aria-label="Eliminar"
                      onClick={async () => { if (await dlg.confirmar(`¿Eliminar "${d.nombre}"?`, { peligro: true })) eliminar.mutate(d.id); }}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {form && (
        <Modal title={form.id ? 'Editar dispositivo' : 'Nuevo dispositivo'} onClose={() => setForm(null)}>
          <div className="space-y-3">
            <Campo label="Nombre *"><input className={fieldCls} value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} placeholder="TV recepción, TV caja 1…" /></Campo>
            <Campo label="Caja (para el modo cliente)">
              <select className={fieldCls} value={form.caja_id} onChange={(e) => setForm({ ...form, caja_id: e.target.value })}>
                <option value="">— Sin caja (solo vitrina) —</option>
                {cajas.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
              </select>
            </Campo>
            <Campo label="Plantilla">
              <select className={fieldCls} value={form.plantilla_id} onChange={(e) => setForm({ ...form, plantilla_id: e.target.value })}>
                <option value="">— Por defecto (vitrina) —</option>
                {plantillas.map((p) => <option key={p.id} value={p.id}>{p.nombre}</option>)}
              </select>
            </Campo>
            <Switch checked={form.es_activo} onChange={(v) => setForm({ ...form, es_activo: v })} label="Activo" />
            <p className="text-xs text-muted-foreground">Tras guardar, copia la URL y ábrela en la TV. La pantalla solo muestra productos no sensibles; nunca nombre ni historial del cliente.</p>
            <div className="flex justify-end gap-2 border-t pt-3">
              <Button variant="outline" onClick={() => setForm(null)}>Cancelar</Button>
              <Button isLoading={guardar.isPending} disabled={!form.nombre.trim()} onClick={() => guardar.mutate(form)}>Guardar</Button>
            </div>
          </div>
        </Modal>
      )}

      {progDisp && <ProgramacionModal device={progDisp} plantillas={plantillas} onClose={() => setProgDisp(null)} />}
    </div>
  );
}

const DIAS = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
interface Regla { id: string; dia_semana: number | null; hora_inicio: string; hora_fin: string; plantilla_id: string; plantilla: string | null; prioridad: number; es_activo: boolean }
const reglaVacia = { dia_semana: '', hora_inicio: '08:00', hora_fin: '20:00', plantilla_id: '', prioridad: '0' };

function ProgramacionModal({ device, plantillas, onClose }: { device: Dispositivo; plantillas: Plantilla[]; onClose: () => void }) {
  const qc = useQueryClient();
  const key = ['tvProg', device.id];
  const { data: reglas = [] } = useQuery<Regla[]>({ queryKey: key, queryFn: () => rpc<Regla[]>('tv_programacion', { _dispositivo: device.id }) });
  const [f, setF] = useState({ ...reglaVacia });

  const agregar = useMutation({
    mutationFn: () => rpc('tv_programacion_guardar', { _data: { dispositivo_id: device.id, dia_semana: f.dia_semana === '' ? null : Number(f.dia_semana), hora_inicio: f.hora_inicio, hora_fin: f.hora_fin, plantilla_id: f.plantilla_id, prioridad: Number(f.prioridad) } }),
    onSuccess: () => { setF({ ...reglaVacia }); void qc.invalidateQueries({ queryKey: key }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const quitar = useMutation({
    mutationFn: (id: string) => rpc('tv_programacion_eliminar', { _id: id }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: key }),
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Modal title={`Programación · ${device.nombre}`} onClose={onClose} wide>
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground">Define qué plantilla se muestra según día y franja horaria. Si ninguna regla aplica, se usa la plantilla por defecto del dispositivo.</p>
        {reglas.length > 0 && (
          <ul className="text-sm divide-y border rounded-md">
            {reglas.map((r) => (
              <li key={r.id} className="flex items-center justify-between px-3 py-2">
                <span>{r.dia_semana == null ? 'Todos los días' : DIAS[r.dia_semana]} · {r.hora_inicio?.slice(0, 5)}–{r.hora_fin?.slice(0, 5)} → <b>{r.plantilla || '—'}</b>{r.prioridad ? ` (prio ${r.prioridad})` : ''}</span>
                <button className="text-red-500" title="Quitar" aria-label="Quitar" onClick={() => quitar.mutate(r.id)}><Trash2 className="w-4 h-4" /></button>
              </li>
            ))}
          </ul>
        )}
        <div className="grid grid-cols-12 items-end gap-2">
          <div className="col-span-3"><Campo label="Día"><select className={fieldCls} value={f.dia_semana} onChange={(e) => setF({ ...f, dia_semana: e.target.value })}><option value="">Todos</option>{DIAS.map((d, i) => <option key={i} value={i}>{d}</option>)}</select></Campo></div>
          <div className="col-span-2"><Campo label="Desde"><input type="time" className={fieldCls} value={f.hora_inicio} onChange={(e) => setF({ ...f, hora_inicio: e.target.value })} /></Campo></div>
          <div className="col-span-2"><Campo label="Hasta"><input type="time" className={fieldCls} value={f.hora_fin} onChange={(e) => setF({ ...f, hora_fin: e.target.value })} /></Campo></div>
          <div className="col-span-4"><Campo label="Plantilla"><select className={fieldCls} value={f.plantilla_id} onChange={(e) => setF({ ...f, plantilla_id: e.target.value })}><option value="">— Elegir —</option>{plantillas.map((p) => <option key={p.id} value={p.id}>{p.nombre}</option>)}</select></Campo></div>
          <div className="col-span-1"><Button size="sm" isLoading={agregar.isPending} disabled={!f.plantilla_id} onClick={() => agregar.mutate()}>Add</Button></div>
        </div>
        <div className="flex justify-end border-t pt-3"><Button variant="outline" onClick={onClose}>Cerrar</Button></div>
      </div>
    </Modal>
  );
}
